import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

import wt from "./../../assets/local.jpg";
import netflix from "./../../assets/netflix.png";
import akptc from "./../../assets/akptc.jpg";
import respc from "./../../assets/respc.jpg";
import rediweb from "./../../assets/rediweb.jpg";

const PROJECTS = [
  {
    title: "Wara Technology",
    desc: "Corporate website built with modern UI and performance in mind.",
    image: wt,
    link: "https://waratechnology.com/",
  },
  {
    title: "Yene Restaurant",
    desc: "Restaurant website with clean layout and smooth user flow.",
    image: respc,
    link: "https://yenerestaurant.netlify.app/",
  },
  {
    title: "Rediet Feleke",
    desc: "Personal branding and professional portfolio website.",
    image: rediweb,
    link: "https://redietfeleke.com.et/",
  },
  {
    title: "AKPTC",
    desc: "Educational institution website with modern UX.",
    image: akptc,
    link: "https://akptc.et/",
  },
  {
    title: "Netflix Clone",
    desc: "Streaming platform UI clone for learning purposes.",
    image: netflix,
    link: "https://onyx-basis-395607.web.app",
  },
];

const ITEMS_PER_PAGE_DESKTOP = 6;

/* animation variants like Contact page */
const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.12,
      ease: "easeOut",
      duration: 0.6,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const MyWork = () => {
  const scrollRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const [page, setPage] = useState(0);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!isMobile || !scrollRef.current) return;

    const el = scrollRef.current;
    const onScroll = () => {
      const index = Math.round(el.scrollLeft / el.offsetWidth);
      setPage(index);
    };

    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [isMobile]);

  const totalPages = Math.ceil(PROJECTS.length / ITEMS_PER_PAGE_DESKTOP);

  const desktopProjects = PROJECTS.slice(
    page * ITEMS_PER_PAGE_DESKTOP,
    page * ITEMS_PER_PAGE_DESKTOP + ITEMS_PER_PAGE_DESKTOP
  );

  return (
    <section id="work" className="py-24 px-6 md:px-16 lg:px-28">
      {/* TITLE - smooth like Contact */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        className="text-center mb-16"
      >
        <h2 className="text-4xl md:text-6xl font-semibold bg-gradient-to-r from-orange-400 to-purple-500 bg-clip-text text-transparent">
          My Works
        </h2>
        <p className="mt-4 text-neutral-400 max-w-xl mx-auto">
          Selected projects crafted with focus on UX, performance and clarity.
        </p>
      </motion.div>

      {/* MOBILE */}
      {isMobile && (
        <>
          <motion.div
            ref={scrollRef}
            variants={containerVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="flex gap-6 overflow-x-auto snap-x snap-mandatory pb-6 scrollbar-hide"
          >
            {PROJECTS.map((project, index) => (
              <motion.a
                key={index}
                href={project.link}
                target="_blank"
                rel="noopener noreferrer"
                variants={cardVariants}
                whileTap={{ scale: 0.96 }}
                className="snap-center min-w-[85%]"
              >
                <ProjectCard project={project} />
              </motion.a>
            ))}
          </motion.div>

          <div className="mt-6 flex justify-center gap-2">
            {PROJECTS.map((_, i) => (
              <span
                key={i}
                className={`w-2.5 h-2.5 rounded-full transition ${
                  page === i ? "bg-white scale-125" : "bg-white/30"
                }`}
              />
            ))}
          </div>
        </>
      )}

      {/* DESKTOP */}
      {!isMobile && (
        <>
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-10"
          >
            {desktopProjects.map((project, index) => (
              <motion.a
                key={index}
                href={project.link}
                target="_blank"
                rel="noopener noreferrer"
                variants={cardVariants}
                whileHover={{ y: -12, scale: 1.01 }}
                transition={{ type: "spring", stiffness: 200, damping: 20 }}
              >
                <ProjectCard project={project} />
              </motion.a>
            ))}
          </motion.div>

          <div className="mt-14 flex justify-center gap-3">
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={i}
                onClick={() => setPage(i)}
                className={`w-10 h-10 rounded-full transition ${
                  page === i
                    ? "bg-white text-black"
                    : "border border-white/10 text-neutral-400 hover:border-white/30"
                }`}
              >
                {i + 1}
              </button>
            ))}
          </div>
        </>
      )}
    </section>
  );
};

const ProjectCard = ({ project }) => (
  <motion.div
    whileHover={{ scale: 1.02 }}
    transition={{ duration: 0.4, ease: "easeOut" }}
    className="group h-full rounded-3xl bg-neutral-900 border border-white/10 overflow-hidden"
  >
    <div className="h-52 bg-black flex items-center justify-center">
      <img
        src={project.image}
        alt={project.title}
        className="
          max-w-full max-h-full object-contain
          transition-transform duration-700 ease-out
          group-hover:scale-105 
        "
      />
    </div>

    <div className="p-6">
      <h3 className="text-lg font-semibold text-white gradient-text">
        {project.title}
      </h3>
      <p className="text-sm text-neutral-400 mt-2 leading-relaxed">
        {project.desc}
      </p>
      <span className="inline-block mt-5 text-sm text-neutral-500 group-hover:text-white transition">
        View project →
      </span>
    </div>
  </motion.div>
);

export default MyWork;
